import React from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import {
  ProductBacklogRow,
  isProductBacklogItemRow,
  isSprintRow,
} from '../productBacklog'
import { styled } from '@mui/material'
import '../../../containers/commons/AgGrid/components/cell/common/iconText/style.scss'
import { colorPalette } from '../../../style/colorPalette'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { ProjectPlanTreeCellRenderer } from '../../../containers/commons/AgGrid/components/cell/custom/wbsItemName/projectPlanTreeCellRenderer'
import { dateV2Service } from '../../../../domain/value-object/DateV2'
import { WbsItemCellRenderer } from '../../../containers/commons/AgGrid/components/cell/custom/wbsItemName/wbsItemCellRenderer'

interface ProductBacklogNameCellRendererProps
  extends ICellRendererParams<ProductBacklogRow> {
  onClickSprintEstimates?: (sprintId: string) => void
}

const formatSprintName = (
  name: string,
  sprintDetail?: { startDate?: string; endDate?: string }
) => {
  if (!sprintDetail?.startDate || !sprintDetail?.endDate) {
    return name
  }

  const startDate = dateV2Service.formatMonthAndDate(
    dateV2Service.newDateFromString(sprintDetail.startDate)
  )
  const endDate = dateV2Service.formatMonthAndDate(
    dateV2Service.newDateFromString(sprintDetail.endDate)
  )
  return `${name}（${startDate}～${endDate}）`
}

export const ProductBacklogNameCellRenderer: React.FC<
  ProductBacklogNameCellRendererProps
> = props => {
  const { data, onClickSprintEstimates } = props

  if (!data) return <></>

  // スプリントは標準的な表示（カレンダーアイコン付き）
  if (isSprintRow(data)) {
    const displayName = formatSprintName(data.name, data.sprintDetail)
    // スプリント未設定の場合は'unassigned-sprint'を、それ以外はuuidを使用
    const sprintId = data.isUnset ? 'unassigned-sprint' : data.uuid

    return (
      <SprintContainer>
        <button
          className="sevend-ag-cell-icon-text__icon--button"
          style={{ color: colorPalette.blue[5] }}
          onClick={
            onClickSprintEstimates && sprintId
              ? e => {
                  e.stopPropagation()
                  onClickSprintEstimates(sprintId)
                }
              : undefined
          }
        >
          <CalendarMonthIcon fontSize="small" />
        </button>
        <SprintName>{displayName}</SprintName>
      </SprintContainer>
    )
  }

  // 成果物はWBSの検索で使用しているProjectPlanTreeCellRendererを使用
  if (isProductBacklogItemRow(data)) {
    const displayName =
      data.wbsItem.displayName || data.wbsItem.code || 'Unnamed Item'

    // ProjectPlanTreeCellRendererが期待するデータ構造に合わせる
    const modifiedData = {
      ...data,
      body: {
        wbsItem: data.wbsItem,
        commentSummary: undefined, // プロダクトバックログではコメントサマリーは不要
      },
    }

    return (
      <WbsItemContainer>
        <WbsItemCellRenderer
          {...props}
          value={displayName}
          data={modifiedData}
        />
      </WbsItemContainer>
    )
  }

  return <></>
}

const SprintContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
})

const SprintName = styled('span')({
  flex: 1,
})

const WbsItemContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
})
